// src/components/QuestionRunner.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Stack, Button, TextField, CircularProgress } from '@mui/material';
import { supabase } from '../supabaseClient.ts';
import RichText from './RichText.tsx';
import {
  choicesAsFlatStrings,
  getTableChoiceData,
  isMultipleChoiceQuestion,
} from '../utils/questionChoices.ts';
import { McChoiceTableStudent } from './McChoiceTableStudent.tsx';

type Q = {
  id: string;
  prompt: string;
  type: 'mc' | 'input' | 'table';
  choices: any | null;
  answer: string | null;
  answer_index: number | null;
  worked_solution: string | null;
  group?: { id: string; prompt: string } | null;
};

const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

const RunnerAnswerBox: React.FC<{ q: Q }> = ({ q }) => {
  const tableMc = getTableChoiceData(q.choices);
  const isMC = isMultipleChoiceQuestion(q.type, q.choices);
  const choices = choicesAsFlatStrings(q.choices);
  const correctIdx = q.answer_index ?? null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1.5, borderLeft: 4, borderLeftColor: 'success.main' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Answer
      </Typography>

      {isMC ? (
        <>
          {correctIdx != null && (
            <Typography variant="body1" sx={{ mb: tableMc ? 1 : choices[correctIdx] ? 0.5 : 0 }}>
              Correct: <strong>{letters[correctIdx] ?? String(correctIdx + 1)}</strong>
            </Typography>
          )}
          {tableMc ? (
            <McChoiceTableStudent
              readOnly
              columns={tableMc.columns}
              rows={tableMc.rows}
              selectedIndex={correctIdx ?? undefined}
              onSelect={() => {}}
              revealAnswers={correctIdx != null}
              correctIndex={correctIdx}
              renderCell={(html) => <RichText html={html} />}
            />
          ) : choices[correctIdx ?? -1] ? (
            <RichText html={choices[correctIdx!]} />
          ) : q.answer ? (
            <RichText html={q.answer} />
          ) : null}
        </>
      ) : (
        <>
          {q.answer && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Accepted answer
              </Typography>
              <RichText html={q.answer} />
            </>
          )}
        </>
      )}

      {q.worked_solution && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            Worked solution
          </Typography>
          <RichText html={q.worked_solution} />
        </>
      )}
      {!isMC && !q.answer && !q.worked_solution && (
        <Typography variant="body2" color="text.secondary">
          No solution available yet.
        </Typography>
      )}
    </Paper>
  );
};

const QuestionRunner: React.FC<{ id: string }> = ({ id }) => {
  const [q, setQ] = useState<Q | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [shortAnswer, setShortAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data, error } = await supabase
          .from('questions')
          .select('id,prompt,type,choices,answer,answer_index,worked_solution, group:question_groups(id,prompt)')
          .eq('id', id)
          .single();
        if (error) throw error;
        if (!cancelled)
          setQ({
            id: data.id,
            prompt: data.prompt,
            type: data.type,
            choices: data.choices,
            answer: data.answer,
            answer_index: data.answer_index,
            worked_solution: data.worked_solution,
            group: Array.isArray(data.group) ? (data.group[0] ?? null) : (data.group ?? null),
          });
      } catch (e: any) {
        if (!cancelled) setErr(e.message || 'Failed to load question.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    setSelectedIndex(null);
    setShortAnswer('');
    setShowAnswer(false);
  }, [id]);

  const choices = useMemo(() => choicesAsFlatStrings(q?.choices), [q]);
  const isMC = q ? isMultipleChoiceQuestion(q.type, q.choices) : false;
  const tableMc = useMemo(() => getTableChoiceData(q?.choices ?? null), [q?.choices]);

  if (loading)
    return (
      <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <CircularProgress size={18} /> Loading…
      </Box>
    );
  if (err) return <Typography color="error" sx={{ p: 2 }}>{err}</Typography>;
  if (!q) return null;

  return (
    <Box sx={{ my: 2.5 }}>
      {q.group?.prompt && (
        <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary">
            Shared prompt
          </Typography>
          <RichText html={q.group.prompt} />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <RichText html={q.prompt} />

        {isMC &&
          (tableMc ? (
            <Box sx={{ mt: 2 }}>
              <McChoiceTableStudent
                columns={tableMc.columns}
                rows={tableMc.rows}
                selectedIndex={selectedIndex ?? undefined}
                onSelect={(i) => setSelectedIndex(i)}
                renderCell={(html) => <RichText html={html} />}
                revealAnswers={showAnswer}
                correctIndex={q.answer_index}
              />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {choices.map((choice, i) => {
                const isCorrect = q.answer_index != null && i === q.answer_index;
                const isSelected = selectedIndex === i;
                const borderColor =
                  isCorrect && showAnswer
                    ? 'success.main'
                    : isSelected && !showAnswer
                      ? 'primary.main'
                      : 'divider';

                return (
                  <Paper
                    key={i}
                    variant="outlined"
                    onClick={() => setSelectedIndex(i)}
                    sx={{
                      p: 1.25,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      cursor: 'pointer',
                      borderColor,
                      borderWidth: isCorrect && showAnswer ? 2 : 1,
                      bgcolor: isSelected && !showAnswer ? 'action.hover' : 'background.paper',
                      transition: 'background 120ms, border-color 120ms, border-width 120ms',
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '999px',
                        border: '1px solid',
                        borderColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        flex: '0 0 auto',
                        mt: 0.25,
                      }}
                    >
                      {letters[i] ?? String(i + 1)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <RichText html={choice} />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ))}

        {!isMC && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Your answer"
              value={shortAnswer}
              onChange={(e) => setShortAnswer(e.target.value)}
            />
          </Box>
        )}

        <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" color={showAnswer ? 'primary' : 'inherit'} onClick={() => setShowAnswer((v) => !v)}>
            {showAnswer ? 'Hide answer' : 'Reveal answer'}
          </Button>
        </Stack>

        {showAnswer && <RunnerAnswerBox q={q} />}
      </Paper>
    </Box>
  );
};

export default QuestionRunner;
